import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Chip,
  Link,
  Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNotify } from 'react-admin';
import { getToken } from '../authentication/authProvider';

const baseUrl = (process.env.REACT_APP_PAPERMAN_API_URL || '').replace(/\/$/, '');

interface BurialPermitPayload {
  lastName: string;
  firstName: string;
  dateOfDeath: string;
  dateOfDisposition: string;
  timeOfDisposition: string;
}

interface Preview {
  funeralId: number;
  payload: BurialPermitPayload;
  dispositionSource: 'cremation_date' | 'estimated_from_dispatch' | 'manual';
  missing: string[];
  warnings: string[];
  documentFilename: string;
}

interface Submission {
  id: string;
  status: string;
  portal_status?: string | null;
  decline_message?: string | null;
  error?: string | null;
  submitted_at?: string | null;
  issued_at?: string | null;
}

/** The bot's steps, in the order the service reports them. */
const STEPS: Array<{ key: string; label: string }> = [
  { key: 'prepare', label: 'Build the combined PDF' },
  { key: 'login', label: 'Sign in to the City portal' },
  { key: 'fill', label: 'Fill the request form' },
  { key: 'upload', label: 'Attach forms' },
  // The City's app uploads the file as part of submitting, so this step covers
  // both and is the slow one.
  { key: 'submit', label: 'Upload and submit' },
  { key: 'done', label: 'Confirmed' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  onSubmitted?: () => void;
}

export default function BurialPermitRequestModal({
  open,
  onClose,
  funeralId,
  onSubmitted,
}: Props) {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ step: string; detail?: string; error?: string }>({
    step: 'idle',
  });
  const [dispositionOverride, setDispositionOverride] = useState('');
  const [dryRunShot, setDryRunShot] = useState<string | null>(null);
  const [submitCheck, setSubmitCheck] = useState<{ found: boolean; detail: string } | null>(null);

  const authedFetch = useCallback(async (path: string, init?: RequestInit) => {
    const token = await getToken();
    return fetch(`${baseUrl}/${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init?.headers || {}),
      },
    });
  }, []);

  const loadPreview = useCallback(
    async (override?: string, opts?: { silent?: boolean; seedDate?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      try {
        const qs = override ? `?dateOfDisposition=${encodeURIComponent(override)}` : '';
        const res = await authedFetch(`burial-permit/toronto/preview/${funeralId}${qs}`);
        if (!res.ok) throw new Error('Could not load the submission preview');
        const data = await res.json();
        setPreview(data.preview);
        setSubmission(data.latestSubmission);
        // Only seed the input on first open; re-seeding while typing would
        // fight the user for the caret.
        if (opts?.seedDate) setDispositionOverride(data.preview?.payload?.dateOfDisposition || '');
      } catch (e: any) {
        if (!opts?.silent) notify(e.message || 'Could not load the submission preview', { type: 'error' });
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [authedFetch, funeralId, notify],
  );

  useEffect(() => {
    if (open) void loadPreview(undefined, { seedDate: true });
  }, [open, loadPreview]);

  // Re-validate against the server whenever the date changes. The blocking
  // checks (past date, missing fields) are computed server-side, so without
  // this the warning and the disabled Begin button reflect the ORIGINAL value
  // and never clear no matter what is typed.
  useEffect(() => {
    if (!open || !dispositionOverride || running) return;
    if (dispositionOverride === preview?.payload?.dateOfDisposition) return;
    const t = setTimeout(() => {
      void loadPreview(dispositionOverride, { silent: true });
    }, 400);
    return () => clearTimeout(t);
  }, [dispositionOverride, open, running, preview?.payload?.dateOfDisposition, loadPreview]);

  // While a run is in flight the service holds progress in memory, so poll it.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const res = await authedFetch(`burial-permit/toronto/status/${funeralId}`);
          if (!res.ok) return;
          const data = await res.json();
          setProgress(data.progress || { step: 'idle' });
          setSubmission(data.submission);
          if (data.submission?.status && data.submission.status !== 'submitting') {
            setRunning(false);
          }
        } catch {
          /* transient — keep polling */
        }
      })();
    }, 1500);
    return () => clearInterval(timer);
  }, [running, authedFetch, funeralId]);

  /** Fill the City's form and stop at Submit. Files nothing. */
  const handleDryRun = async () => {
    setRunning(true);
    setDryRunShot(null);
    setSubmitCheck(null);
    setProgress({ step: 'prepare' });
    try {
      const res = await authedFetch(`burial-permit/toronto/dry-run/${funeralId}`, {
        method: 'POST',
        body: JSON.stringify({ dateOfDisposition: dispositionOverride || undefined }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Dry run failed');
      setDryRunShot(data.screenshotBase64 || null);
      setSubmitCheck(data.submitCheck || null);
      notify('Dry run complete — nothing was filed', { type: 'success' });
    } catch (e: any) {
      notify(e.message || 'Dry run failed', { type: 'error' });
    } finally {
      setRunning(false);
    }
  };

  const handleBegin = async () => {
    setRunning(true);
    setProgress({ step: 'prepare' });
    try {
      const res = await authedFetch(`burial-permit/toronto/submit/${funeralId}`, {
        method: 'POST',
        body: JSON.stringify({
          dateOfDisposition: dispositionOverride || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === 'failed') {
        throw new Error(data.error || data.message || 'Submission failed');
      }
      notify('Burial permit request submitted to the City', { type: 'success' });
      onSubmitted?.();
    } catch (e: any) {
      notify(e.message || 'Submission failed', { type: 'error' });
    } finally {
      setRunning(false);
      void loadPreview(dispositionOverride || undefined, { seedDate: false });
    }
  };

  const blocked = !!preview?.missing?.length;
  const estimated = preview?.dispositionSource === 'estimated_from_dispatch';
  const alreadyOpen =
    submission?.status === 'submitted' || submission?.status === 'submitting';

  const row = (label: string, value: React.ReactNode) => (
    <TableRow>
      <TableCell sx={{ width: 220, color: 'text.secondary', border: 0, py: 0.75 }}>
        {label}
      </TableCell>
      <TableCell sx={{ border: 0, py: 0.75, fontWeight: 500 }}>{value}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Burial Permit</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && preview && (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This is exactly what will be filed with the City of Toronto.
            </Typography>

            <Table size="small">
              <TableBody>
                {row('Last name', preview.payload.lastName)}
                {row('First name', preview.payload.firstName)}
                {row('Date of death', preview.payload.dateOfDeath)}
                {row(
                  'Date of disposition',
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      type="date"
                      size="small"
                      value={dispositionOverride}
                      onChange={(e) => setDispositionOverride(e.target.value)}
                      disabled={running}
                      sx={{ width: 175 }}
                    />
                    {estimated && !running && (
                      <Chip label="Estimated" size="small" color="warning" variant="outlined" />
                    )}
                  </Box>,
                )}
                {row('Time of disposition', preview.payload.timeOfDisposition)}
                {row(
                  'Upload',
                  <Link
                    href={`${baseUrl}/burial-permit/toronto-permit-docs/${funeralId}`}
                    target="_blank"
                    rel="noopener"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {preview.documentFilename}
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>,
                )}
              </TableBody>
            </Table>

            {blocked && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Not ready to submit</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {preview.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {!blocked && preview.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {preview.warnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </Alert>
            )}

            {alreadyOpen && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A request is already with the City
                {submission?.portal_status ? ` (${submission.portal_status})` : ''}. Wait for
                their response before resubmitting.
              </Alert>
            )}

            {submission?.status === 'declined' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>The City declined the last request</AlertTitle>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                  {submission.decline_message || 'No reason given.'}
                </Typography>
              </Alert>
            )}

            {submission?.status === 'failed' && !running && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>The last attempt failed before reaching the City</AlertTitle>
                <Typography variant="body2">{submission.error}</Typography>
              </Alert>
            )}

            {submitCheck && (
              <Alert severity={submitCheck.found ? 'success' : 'error'} sx={{ mt: 2 }}>
                <AlertTitle>
                  {submitCheck.found
                    ? 'Submit button resolves — the real run would reach it'
                    : 'Submit button NOT found — the real run would fail here'}
                </AlertTitle>
                <Typography variant="caption" component="div" sx={{ wordBreak: 'break-word' }}>
                  {submitCheck.detail}
                </Typography>
              </Alert>
            )}

            {dryRunShot && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  The City's form, filled and stopped at Submit. Nothing was filed.
                </Typography>
                <Box
                  component="img"
                  src={`data:image/png;base64,${dryRunShot}`}
                  alt="Filled burial permit form"
                  sx={{ width: '100%', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                />
              </>
            )}

            {running && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  {STEPS.map((s) => {
                    const currentIndex = STEPS.findIndex((x) => x.key === progress.step);
                    const thisIndex = STEPS.findIndex((x) => x.key === s.key);
                    const done = currentIndex > thisIndex;
                    const active = currentIndex === thisIndex;
                    return (
                      <Box key={s.key} display="flex" alignItems="center" gap={1} py={0.5}>
                        {active ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              bgcolor: done ? 'success.main' : 'action.disabledBackground',
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={active || done ? 'text.primary' : 'text.secondary'}
                        >
                          {s.label}
                        </Typography>
                      </Box>
                    );
                  })}
                  {progress.detail && (
                    <Typography variant="caption" color="text.secondary">
                      {progress.detail}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          Close
        </Button>
        <Button
          onClick={handleDryRun}
          disabled={loading || running || blocked}
        >
          {running ? 'Running…' : 'Test run'}
        </Button>
        <Button
          variant="contained"
          onClick={handleBegin}
          disabled={loading || running || blocked || alreadyOpen}
        >
          {running ? 'Submitting…' : 'Begin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
